/**
 * Automations API — the builder's save/load layer.
 * Backend: gmb-server /api/automation (Automation model), fully live.
 *
 * One-time migration only: if a browser ever saved an automation only to
 * localStorage (from before this endpoint was deployed), FETCH_AUTOMATIONS
 * adopts those leftover rows into the server on the next successful load
 * and reports how many were recovered. Every other call below is a plain
 * network request — failures propagate as normal thrown errors for the
 * UI's existing toast.error handlers to catch.
 */
import { request } from "../helpers/axios.new";

const BASE = "/automation";
const LS_KEY = "gmb_automations_local";

// `request` resolves the whole Axios response; every call here wants the body.
const send = async (options) => (await request(options))?.data;

const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || [];
  } catch {
    return [];
  }
};
const writeLocal = (rows) => localStorage.setItem(LS_KEY, JSON.stringify(rows));

// --- one-time migration of pre-existing localStorage rows -----------------
// POST each leftover local row to the real endpoint; drop it from
// localStorage only once confirmed saved server-side. Rows that fail to
// adopt (e.g. a mid-load connectivity blip) stay local and retry on the
// next page load. CREATE/UPDATE/TOGGLE/DELETE below no longer know
// `local_` ids exist at all — this is the only place they're handled.
const adoptLocalRows = async () => {
  const rows = readLocal();
  if (!rows.length) return { adopted: [], remaining: rows };
  const results = await Promise.all(
    rows.map(async ({ _id, updatedAt, ...data }) => {
      try {
        const created = await send({ url: BASE, method: "POST", data });
        return { ok: true, localId: _id, created };
      } catch {
        return { ok: false, localId: _id };
      }
    })
  );
  const adoptedIds = new Set(results.filter((r) => r.ok).map((r) => r.localId));
  const remaining = rows.filter((r) => !adoptedIds.has(r._id));
  if (adoptedIds.size) writeLocal(remaining);
  return { adopted: results.filter((r) => r.ok).map((r) => r.created), remaining };
};

// Caches the migration attempt for the page's lifetime so repeated
// FETCH_AUTOMATIONS calls (StrictMode double-invoke, workspaceId changes,
// remounts) never double-POST the same local row, and only the first
// caller to observe a nonzero count triggers the "recovered" toast.
let migrationPromise = null;
let migrationNotified = false;
const migrateOnce = async () => {
  if (!migrationPromise) migrationPromise = adoptLocalRows();
  const { adopted, remaining } = await migrationPromise;
  const recoveredCount = migrationNotified ? 0 : adopted.length;
  migrationNotified = true;
  return { adopted, remaining, recoveredCount };
};

export const FETCH_AUTOMATIONS = async (groupId) => {
  const url = groupId ? `${BASE}?groupId=${groupId}` : BASE;
  const { adopted, remaining, recoveredCount } = await migrateOnce();
  const serverRows = await send({ url, method: "GET" });
  const byId = new Map();
  (Array.isArray(serverRows) ? serverRows : []).forEach((r) => byId.set(String(r._id), r));
  adopted.forEach((r) => r && byId.set(String(r._id), r));
  const localLeftover = remaining.filter((a) => !groupId || String(a.groupId) === String(groupId));
  return { rows: [...localLeftover, ...Array.from(byId.values())], recoveredCount };
};

export const CREATE_AUTOMATION = (data) => send({ url: BASE, method: "POST", data });

export const UPDATE_AUTOMATION = (id, data) => send({ url: `${BASE}/${id}`, method: "PUT", data });

export const TOGGLE_AUTOMATION = (id, active) =>
  send({ url: `${BASE}/${id}/toggle`, method: "PATCH", data: { active } });

// Execution log — one row per firing of this automation, written by the engine.
export const FETCH_AUTOMATION_RUNS = async (id) => {
  const rows = await send({ url: `${BASE}/${id}/runs`, method: "GET" });
  return Array.isArray(rows) ? rows : [];
};

export const DELETE_AUTOMATION = (id) => send({ url: `${BASE}/${id}`, method: "DELETE" });
